import { Helmet } from 'react-helmet-async';
import { SITE } from '../config/seo';

/**
 * Reusable SEO head component.
 * Always emits: page-specific <title>, description, canonical, hreflang
 * (ar-SA, ar-AE, ar-KW, ar-BH, ar-QA, ar-OM, en, x-default), Open Graph,
 * Twitter Card, and any page-specific JSON-LD schemas passed via `schemas`.
 *
 * Note: Organization + WebSite + SoftwareApplication JSON-LD are baked into
 * /app/frontend/public/index.html as a static @graph so non-JS crawlers
 * (LLM bots, social-media OG fetchers, Bingbot) see them. We do NOT duplicate
 * them here.
 *
 * Usage:
 *   <SEOHead
 *     title="..."  titleEn="..."
 *     description="..." descriptionEn="..."
 *     path="/restaurants"
 *     keywords={["..."]}
 *     schemas={[faqSchema, productSchema, breadcrumbSchema]}
 *     lang="ar"
 *   />
 */
export default function SEOHead({
  title,
  titleEn,
  description,
  descriptionEn,
  path = '/',
  keywords = [],
  image,
  schemas = [],
  lang = 'ar',
  noIndex = false,
}) {
  const isAr = lang === 'ar';
  const finalTitle = isAr ? title : (titleEn || title);
  const finalDesc = isAr ? description : (descriptionEn || description);
  const canonical = `${SITE.url}${path}`;
  const ogImage = image || (isAr ? SITE.ogImageAr : SITE.ogImage);
  const ogLocale = isAr ? 'ar_SA' : 'en_US';

  return (
    <Helmet>
      {/* Primary */}
      <html lang={lang} dir={isAr ? 'rtl' : 'ltr'} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      <link rel="canonical" href={canonical} />
      {noIndex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />}

      {/* hreflang for AR variants and EN, plus x-default */}
      <link rel="alternate" hrefLang="ar-SA" href={canonical} />
      <link rel="alternate" hrefLang="ar-AE" href={canonical} />
      <link rel="alternate" hrefLang="ar-KW" href={canonical} />
      <link rel="alternate" hrefLang="ar-BH" href={canonical} />
      <link rel="alternate" hrefLang="ar-QA" href={canonical} />
      <link rel="alternate" hrefLang="ar-OM" href={canonical} />
      <link rel="alternate" hrefLang="en" href={`${canonical}?lang=en`} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Geo - Gulf focus */}
      <meta name="geo.region" content="SA" />
      <meta name="geo.placename" content="Riyadh, Jeddah, Dammam, Dubai, Kuwait, Manama, Doha, Muscat" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="revisit-after" content="7 days" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.brandAr} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content="ar_AE" />
      <meta property="og:locale:alternate" content="ar_KW" />
      <meta property="og:locale:alternate" content="ar_BH" />
      <meta property="og:locale:alternate" content="ar_QA" />
      <meta property="og:locale:alternate" content="ar_OM" />
      <meta property="og:locale:alternate" content="en_US" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={finalTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE.twitter} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={ogImage} />

      {/* AI / LLM hints */}
      <meta name="ai-content-declaration" content="Software product page describing AI-powered restaurant management features." />

      {/* Page-specific JSON-LD Schema.org (Product / FAQ / Breadcrumb).
          Organization + WebSite + SoftwareApplication live in index.html
          static @graph for non-JS crawler visibility. */}
      {schemas.map((schema, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
